import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../../../lib/supabase'
import type { Message } from '../../../../types/chat.types'
import { useLLMContext } from '../../../../contexts/LLMContext'

/**
 * Hook to manage Agent-based chat interactions.
 * Replaces useRagChat for the new LangGraph architecture.
 * 
 * Features:
 * - Persists history to Supabase 'messages' table
 * - Uses LLMContext to route requests to Agent API (:8001)
 * - Handless SSE Streaming from Agent
 */
export const useAgentChat = (
    conversationId: string | null,
    userId: string | null,
    agentId: string | null
) => {
    const [messages, setMessages] = useState<Message[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Global context that knows how to talk to Backend
    const { sendMessage: sendToBackend } = useLLMContext()

    // 1. Load History (Same logic as useRagChat to maintain compatibility)
    const loadMessages = useCallback(async () => {
        if (!conversationId) {
            setMessages([])
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const { data, error: loadError } = await supabase
                .from('messages')
                .select('*')
                .eq('conversation_id', conversationId)
                .order('created_at', { ascending: true })

            if (loadError) throw loadError

            const dbMessages = (data as unknown as Message[]) || []
            setMessages(dbMessages)
        } catch (err) {
            console.error('[useAgentChat] Error loading messages:', err)
            setError(err instanceof Error ? err.message : 'Failed to load messages')
        } finally {
            setIsLoading(false)
        }
    }, [conversationId])

    useEffect(() => {
        loadMessages()
    }, [loadMessages])

    const isSendingRef = useRef(false)

    // 2. Send Message Logic
    const sendMessage = useCallback(async (content: string) => {
        if (isSendingRef.current) return null
        if (!conversationId || !userId || !agentId) return null

        setIsSending(true)
        isSendingRef.current = true
        setError(null)

        try {
            // A. Optimistic User Message & Save to DB
            const userMsgData = {
                conversation_id: conversationId,
                content,
                role: 'user' as const,
                created_at: new Date().toISOString()
            }

            const { data: savedUserMessage, error: sendError } = await supabase
                .from('messages')
                .insert(userMsgData)
                .select()
                .single()

            if (sendError) throw sendError

            // Update UI immediately
            setMessages(prev => [...prev, savedUserMessage as unknown as Message])

            // B. Prepare Assistant Placeholder
            // We create a temporary ID to stream into
            const tempAssistantId = `temp-${Date.now()}`
            const assistantPlaceholder: Message = {
                id: tempAssistantId,
                conversation_id: conversationId,
                role: 'assistant',
                content: '', // Starts empty
                created_at: new Date().toISOString(),
                is_html: false
            }

            setMessages(prev => [...prev, assistantPlaceholder])

            // C. Call LLMContext (Agent API)
            // We interpret history for the agent context if needed, but Agent usually manages its own memory.
            // For MVP, we send just the new message (or history if backend is stateless). 
            // LangGraph usually has a Checkpointer, so sending conversation_id in configs is enough.
            // But ChatRequest schema expects 'messages' list.

            // Convert current history to format expected by backend if needed, 
            // OR just send the last user message if Agent has memory. 
            // SAFEST: Send just the new message for now and let the backend handle thread_id routing hopefully?
            // Wait, looking at chat.py, inputs = {"messages": lc_messages}. 
            // If backend graph has memory (checkpointer), it appends.
            // Assuming simplified flow: prompt = messages[-1]. 

            // Let's send the user message.
            const messagesPayload = [{ role: 'user', content }]

            // The LLMContext.sendMessage returns raw Response for agents
            const response = await sendToBackend(messagesPayload, { agentId })

            if (!(response instanceof Response)) {
                // If it wasn't a Response object (not streamed?), handle fallback
                throw new Error("Expected streaming response from Agent Backend")
            }

            if (!response.body) throw new Error("No response body")

            // D. Streaming Handler
            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let fullContent = ''

            try {
                while (true) {
                    const { value, done } = await reader.read()
                    if (done) break

                    const chunk = decoder.decode(value)
                    // Parse SSE format: "data: {...}"
                    const lines = chunk.split('\n\n')

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const dataStr = line.slice(6)
                            if (dataStr === '[DONE]') continue

                            try {
                                const parsed = JSON.parse(dataStr)
                                // OpenAI format: choices[0].delta.content
                                const contentDelta = parsed.choices?.[0]?.delta?.content

                                if (contentDelta) {
                                    fullContent += contentDelta
                                    // Update UI with streaming content
                                    setMessages(prev => prev.map(m =>
                                        m.id === tempAssistantId
                                            ? { ...m, content: fullContent }
                                            : m
                                    ))
                                }
                            } catch (e) {
                                console.warn("Error parsing stream chunk", e)
                            }
                        }
                    }
                }
            } finally {
                reader.releaseLock()
            }

            // E. Save Final Assistant Response to DB
            const { data: savedAssistantMessage, error: saveError } = await supabase
                .from('messages')
                .insert({
                    conversation_id: conversationId,
                    role: 'assistant',
                    content: fullContent,
                    created_at: new Date().toISOString()
                })
                .select()
                .single()

            if (!saveError && savedAssistantMessage) {
                // Replace temp message with real DB message
                setMessages(prev => prev.map(m =>
                    m.id === tempAssistantId ? (savedAssistantMessage as unknown as Message) : m
                ))
            }

        } catch (err: any) {
            console.error('[useAgentChat] Error:', err)
            setError(err.message || 'Failed to send message')

            // Save error to DB as system message
            await supabase.from('messages').insert({
                conversation_id: conversationId,
                role: 'system',
                content: `Error: ${err.message}`,
                created_at: new Date().toISOString()
            })
        } finally {
            setIsSending(false)
            isSendingRef.current = false
        }
    }, [conversationId, userId, agentId, sendToBackend])

    const deleteMessage = useCallback(async (messageId: string) => {
        try {
            const { error: deleteError } = await supabase
                .from('messages')
                .delete()
                .eq('id', messageId)

            if (deleteError) throw deleteError
            setMessages(prev => prev.filter(m => m.id !== messageId))
        } catch (err) {
            console.error('[useAgentChat] Error deleting:', err)
        }
    }, [])

    return {
        messages,
        isLoading,
        isSending,
        error,
        sendMessage,
        deleteMessage,
        streamingMessage: null // handled inline
    }
}
